"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";

interface Feature {
  id: string;
  title: string;
  url: string;
  text: string; // Description
  media?: { data?: { attributes?: { url?: string } } }; // Icon fallback
}

interface FeaturesProps {
  data: {
    heading: string;
    description: string;
    isLive: boolean;
    useH1Tag?: boolean;
    feature: Feature[];
  };
}

export default function Features_Editorial_Standard({ data }: FeaturesProps) {
  if (!data?.isLive) return null;

  return (
    <section className="py-24 bg-neutral-50 border-y border-neutral-100 px-6  overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        {/* ===== Professional Editorial Header ===== */}
        <div className="flex flex-col items-center mb-20 space-y-4 text-center">
          <div className="flex items-center gap-3">
            <span className="h-[1px] w-10 bg-[var(--secondary)]"></span>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-gray-700">
              The Quality Standard
            </p>
            <span className="h-[1px] w-10 bg-[var(--secondary)]"></span>
          </div>

          <div className="text-4xl md:text-5xl font-black text-neutral-900 uppercase italic tracking-tighter leading-none">
            {data.useH1Tag ? (
              <h1>
                {data.heading.split(" ").slice(0, -1).join(" ")}{" "}
                <span className="text-[var(--secondary)]">
                  {data.heading.split(" ").pop()}
                </span>
              </h1>
            ) : (
              <h2>
                {data.heading.split(" ").slice(0, -1).join(" ")}{" "}
                <span className="text-[var(--secondary)]">
                  {data.heading.split(" ").pop()}
                </span>
              </h2>
            )}
          </div>
          <p className="text-[11px] font-bold text-gray-700 uppercase tracking-widest max-w-lg">
            {data.description}
          </p>
        </div>

        {/* ===== Feature Grid ===== */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {data.feature.map((item, idx) => {
            // Logic to determine icon: use media if provided, else fallback to a dynamic lookup
            const IconComponent =
              (LucideIcons as any)[item.url] || LucideIcons.Zap;

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="group flex flex-col items-center text-center px-4"
              >
                {/* Minimalist Icon Shell */}
                <div className="relative mb-8 transition-transform duration-500 group-hover:-translate-y-2">
                  <div className="w-20 h-20 bg-white border border-neutral-200 flex items-center justify-center relative z-10">
                    {/* Render Icon or Image */}
                    {item.media?.data?.attributes?.url ? (
                      <div className="relative w-10 h-10">
                        <Image
                          src={item.media.data.attributes.url}
                          alt={item.title}
                          fill
                          className="object-contain"
                        />
                      </div>
                    ) : (
                      <IconComponent
                        className="w-8 h-8 text-neutral-900 group-hover:text-[var(--secondary)] transition-colors"
                        strokeWidth={1.2}
                      />
                    )}
                  </div>

                  {/* Decorative Background Accent (Rose/Secondary) */}
                  <div className="absolute inset-0 bg-[var(--secondary)] translate-x-2 translate-y-2 -z-0 opacity-0 group-hover:opacity-10 transition-opacity" />
                </div>

                {/* Typography Hierarchy */}
                <div className="space-y-3">
                  <h3 className="text-sm font-black text-neutral-900 uppercase tracking-[0.15em]">
                    {item.title}
                  </h3>

                  {/* Interaction Line */}
                  <div className="h-[2px] w-6 bg-[var(--secondary)] mx-auto transition-all duration-500 group-hover:w-12"></div>

                  <p className="text-[11px] font-bold text-gray-700 uppercase tracking-widest leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
